import React, { useState, createContext, useEffect, useRef } from 'react';
import axios from '../Util/axiosConfig';
import { getUser as getLocalUser, setUser as setLocalUser } from '../helpers';
import { baseUrl } from '../Contants';
import { logout, getCustomerCreateAccountStatus } from '../Actions';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

export const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
  const DELAY = 5000;
  const [user, setUser] = useState(getLocalUser());
  const [loading, setLoading] = useState(false);
  // True once the first auth resolution completes. Used so route guards only
  // show the full-screen loader on initial load — not on every background
  // session check, which would otherwise remount the whole page each time.
  const [authInitialized, setAuthInitialized] = useState(false);
  // Hydrate from the persisted user synchronously (same as `user` above). Defaulting
  // to `true` made an already-logged-in user look logged-out on the first render, so
  // the product page fired its fetch on the customer path, then aborted it and re-
  // fetched on the internal path once the mount effect flipped this to false. That
  // cancelled request still runs to completion server-side (SQL isn't cancelled on
  // client abort), wasting a DB connection on every logged-in product load.
  const [isLoggedOut, setIsLoggedOut] = useState(getLocalUser()?.user_id ? false : true);
  const [accessToken, setAccessToken] = useState('');
  const [authType, setAuthType] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState();
  const [roleName, setRoleName] = useState();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [tourMenuOpen, setTourMenuOpen] = useState(false);
  const [rolePrefix, setRolePrefix] = useState();

  const location = useLocation();
  const intervalRef = useRef(null);

  async function handleLogout() {
    setAccessToken('');
    setUser({ access_type_cd: 'c' });
    await logout();
    setIsLoggedOut(true);
    clearInterval(intervalRef.current);
  }

  async function handleSessionCheck() {
    const willExpireAt = localStorage.getItem('tokenExpiration') ? Number(localStorage.getItem('tokenExpiration')) : undefined;
    const googleExpiration = localStorage.getItem('googleExpiration') ? Number(localStorage.getItem('googleExpiration')) : undefined;

    if (!willExpireAt) return;

    const currentTime = new Date().getTime();

    if (currentTime >= willExpireAt) {
      handleLogout();
      return;
    }

    if ((user.auth_type === 'google' || user.auth_type === 'microsoft') && googleExpiration && currentTime >= Number(googleExpiration)) {
      const refreshToken = localStorage.getItem('refreshToken');
      const refreshResult = await axios.get(`${baseUrl}/api/auth/${user.auth_type}/refresh?refreshToken=${encodeURIComponent(refreshToken)}`);

      if (refreshResult.status !== 200 || !refreshResult?.data?.access_token) {
        handleLogout();
        return;
      }

      const expires_in = refreshResult.data.expires_at ? (Number(refreshResult.data.expires_at) - new Date().getTime()) / 1000 : 3600;

      const newUserData = {
        ...user,
        expires_in: expires_in
      }

      localStorage.setItem('googleExpiration', expires_in ? new Date().getTime() + ((expires_in - 30) * 1000) : undefined);
      localStorage.setItem('accessToken', refreshResult.data.access_token);
      // Microsoft rotates refresh tokens; persist the new one when returned.
      if (refreshResult.data.refresh_token) {
        localStorage.setItem('refreshToken', refreshResult.data.refresh_token);
      }

      setUser(newUserData);
      setLocalUser(newUserData);
    }
  }

  useEffect(() => {
    const localUser = getLocalUser();

    setUser(localUser);
    setIsLoggedOut(localUser?.user_id ? false : true);
  }, [])

  useEffect(() => {
    // Create-account status doesn't depend on the auth token — fetch it once so it
    // doesn't re-fire each time accessToken/authType settle during the login flow.
    getCustomerCreateAccountStatus()
      .then((result) => {
        setCanCreate(result.data.data[0].app_value > 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function getUser(url) {
      setLoading(true);

      try {
        const result = await axios.get(url);

        if (result?.status !== 200 || !result?.data?.user_id) {
          setLoading(false);
          setAuthInitialized(true);
          handleLogout();
          return;
        }

        const willExpireAt = localStorage.getItem('tokenExpiration') ? Number(localStorage.getItem('tokenExpiration')) : undefined;
        const localUser = { ...result.data, will_expire_at: willExpireAt ? Number(willExpireAt) : undefined, };

        if ((authType === 'google' || authType === 'microsoft') && result?.data?.expires_in) {
          const googleExpiration = new Date().getTime() + ((Number(result.data.expires_in) - 30) * 1000);
          localStorage.setItem('googleExpiration', googleExpiration);
        }

        setUser(localUser);
        setLocalUser(localUser);
        
        setIsLoggedOut(false);
      } catch (error) {
        handleLogout();
      }

      setLoading(false);
      setAuthInitialized(true);
    }

    if (accessToken && (authType === 'google' || authType === 'microsoft'))
      getUser(`${baseUrl}/api/auth/${authType}/user`);
    if (accessToken && authType === 'local')
      getUser(`${baseUrl}/api/auth/local/user`);
  }, [accessToken, authType]);

  useEffect(() => {
    handleSessionCheck();
  }, [location.pathname]);

  useEffect(() => {
    intervalRef.current = setInterval(() => handleSessionCheck(), DELAY);

    return () => clearInterval(intervalRef.current);
  }, [])

  useEffect(() => {
    async function getRoles() {
      const result = await axios(`${baseUrl}/api/admin/roles`);
      setRoles(result.data.roleList ?? []);
      setRoleId(result.data.requestUserRole?.role_id ?? "");
      setRoleName(result.data.requestUserRole?.role_name ?? "");
      setRolePrefix(result.data.requestUserRole?.prefix ?? "");
      sessionStorage.setItem('roleId', result.data.requestUserRole?.role_id ?? "");
      sessionStorage.setItem('roles', JSON.stringify(result.data.roleList));
    }

    if (user && ['a', 'rw', 'ro'].includes(user.access_type_cd)) {
      getRoles();
    } else {
      // Clear out role information
      setRoles([]);
      setRoleId('');
      setRoleName('');
      setRolePrefix('');
      sessionStorage.removeItem('roleId');
      sessionStorage.removeItem('roles');
    }
    // Depend on stable primitives, not the `user` object reference — each
    // setUser() creates a new object, which previously refetched /roles on
    // every auth/session-check tick even when the user hadn't changed.
  }, [user?.user_id, user?.access_type_cd]);

  return (
    <AppContext.Provider
      value={{
        canCreate,
        user,
        setAccessToken,
        setUser,
        setLoading,
        loading,
        authInitialized,
        setIsLoggedOut,
        isLoggedOut,
        setAuthType,
        roles,
        setRoleId,
        setRoleName,
        roleId,
        roleName,
        rolePrefix,
        setRolePrefix,
        handleLogout,
        whatsNewOpen,
        setWhatsNewOpen,
        tourMenuOpen,
        setTourMenuOpen,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
